import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Brain, Ship, Tv, Megaphone, MapPin, PlayCircle, ArrowRight } from "lucide-react";

/**
 * Servizi in movimento — video reel per i 6 pilastri dell'ecosistema Amazonico 3000.
 * Ogni tile mostra un video Pexels (loop, muted, autoplay) con poster fallback
 * ed overlay editoriale in oro/avorio. I video vengono caricati lazy via IntersectionObserver
 * per performance.
 * Nota: Sono video stock Pexels curati (agroalimentare, cucina, logistica, tech, marketing).
 * Sostituibili in futuro con clip Sora 2 personalizzate.
 */

const SERVICES = [
  {
    icon: ShoppingBag,
    title: "Marketplace B2B",
    lede: "Vetrina digitale 24/7 con 1.200+ etichette e food premium.",
    video: "https://videos.pexels.com/video-files/3196284/3196284-uhd_2560_1440_25fps.mp4",
    poster: "https://images.pexels.com/photos/34736555/pexels-photo-34736555.jpeg",
    link: "/b2b/wines",
    kicker: "01",
  },
  {
    icon: Brain,
    title: "Amazonico AI",
    lede: "Matching predittivo buyer↔prodotto, forecast stock, AI sommelier.",
    video: "https://videos.pexels.com/video-files/5473773/5473773-uhd_2560_1440_30fps.mp4",
    poster: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg",
    link: "/b2b/sommelier",
    kicker: "02",
  },
  {
    icon: Ship,
    title: "Export Services",
    lede: "Dogana, MAPA/ANVISA, container reefer end-to-end.",
    video: "https://videos.pexels.com/video-files/2670/2670-uhd_2560_1440_24fps.mp4",
    poster: "https://images.pexels.com/photos/1094767/pexels-photo-1094767.jpeg",
    link: "/gestionale",
    kicker: "03",
  },
  {
    icon: Tv,
    title: "Amazonico TV",
    lede: "Storytelling video: interviste produttori, degustazioni, QR experience.",
    video: "https://videos.pexels.com/video-files/4058257/4058257-hd_1920_1080_25fps.mp4",
    poster: "https://images.pexels.com/photos/5490778/pexels-photo-5490778.jpeg",
    link: "/tv",
    kicker: "04",
  },
  {
    icon: Megaphone,
    title: "Marketing",
    lede: "Campagne social · SEM · email in 6 lingue, marketing predittivo AI.",
    video: "https://videos.pexels.com/video-files/3251384/3251384-uhd_2732_1440_24fps.mp4",
    poster: "https://images.pexels.com/photos/905163/pexels-photo-905163.jpeg",
    link: "/b2b/marketing",
    kicker: "05",
  },
  {
    icon: MapPin,
    title: "Brazil Desk",
    lede: "Team commerciale locale a San Paolo, eventi tasting, network importatori.",
    video: "https://videos.pexels.com/video-files/4058251/4058251-hd_1920_1080_25fps.mp4",
    poster: "https://images.pexels.com/photos/2049422/pexels-photo-2049422.jpeg",
    link: "/gestionale/crm-leads",
    kicker: "06",
  },
];

export default function ServiceVideosSection() {
  return (
    <section data-testid="service-videos" className="relative py-24 md:py-32 bg-gradient-to-b from-[#f7f1e3] via-[#efe6d0] to-[#f7f1e3]">
      <div className="container-xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A88A4A] font-bold mb-3 flex items-center gap-2">
              <PlayCircle className="h-3.5 w-3.5" /> Servizi in movimento
            </div>
            <h2 className="editorial-heading text-4xl md:text-6xl text-stone-900 leading-[1.05]">
              Sei pilastri.<br />
              <span className="italic text-[#A88A4A]">In video.</span>
            </h2>
          </div>
          <p className="text-sm text-stone-600 max-w-md leading-relaxed">
            Ogni servizio raccontato con immagini in movimento. Marketplace, AI, logistica, media, marketing e presenza locale — l&apos;ecosistema Amazonico 3000 dal vivo.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => <VideoTile key={i} {...s} />)}
        </div>

        <div className="mt-10 text-[10px] uppercase tracking-[0.25em] text-stone-500 text-center">
          Video AI custom · Sora 2 · disponibili su richiesta per il piano Enterprise
        </div>
      </div>
    </section>
  );
}

function VideoTile({ icon: Icon, title, lede, video, poster, link, kicker }) {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = React.useState(false);

  const onMouseEnter = () => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };

  return (
    <Link
      to={link}
      data-testid={`svc-tile-${kicker}`}
      className="group relative aspect-[4/5] overflow-hidden bg-stone-900 text-white transition-all duration-500 hover:shadow-2xl"
      onMouseEnter={onMouseEnter}
    >
      {/* Poster (always) */}
      <img src={poster} alt={title} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? "opacity-0" : "opacity-70"}`} loading="lazy" />

      {/* Video */}
      <video
        ref={videoRef}
        src={video}
        poster={poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${loaded ? "opacity-70 group-hover:opacity-90" : "opacity-0"}`}
      />

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/20" />
      <div className="absolute inset-3 border border-[#C9A961]/0 group-hover:border-[#C9A961]/70 transition-all duration-500 pointer-events-none" />

      {/* Kicker top-left */}
      <div className="absolute top-6 left-6 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#C9A961]" strokeWidth={1.6} />
        <span className="text-[10px] uppercase tracking-[0.35em] text-[#C9A961] font-bold">Servizio {kicker}</span>
      </div>

      {/* Play indicator */}
      <div className="absolute top-6 right-6 opacity-70 group-hover:opacity-100 transition-opacity">
        <PlayCircle className="h-6 w-6 text-white/80" strokeWidth={1.2} />
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
        <h3 className="editorial-heading text-3xl md:text-4xl text-white leading-tight">{title}</h3>
        <p className="text-sm text-white/75 mt-3 leading-relaxed max-w-[90%]">{lede}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C9A961] font-bold group-hover:gap-3 transition-all">
          Scopri il servizio <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
}
